import { useI18n } from "@baton/i18n";
import { ChevronRight, Pin, PinOff } from "@baton/icons";
import { IconButton, useScrollFade } from "@baton/ui";
import { useCallback, useEffect, useId, useState, type CSSProperties, type ReactNode } from "react";
import { Link, NavLink } from "react-router";

/**
 * FR-015-002, FR-015-071, `contracts/screens.md` § Sidebar, `contracts/design-system.md`
 * § The shell: the sidebar frame and its parts. The workspace switcher card is the first row,
 * the rail's current mode supplies the sections between it and the pinned footer, and each
 * collapsible section's expanded state is persisted in local storage. A row is 36 px with its count or
 * tag centred in the trailing slot at its end, on the one axis a branch's disclosure and a section
 * label's control share (2026-10-03), and the count overflows at "99+"; each list is a `GlideList`, whose
 * one highlight glides to the hovered row as Spectrum UI's Tree Nav does.
 */
export interface SidebarItemProps {
  readonly to: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly count?: number;
  readonly countLabel?: string;
  readonly end?: boolean;
  /** Whether the row is the current one, for a row whose destination redirects elsewhere (Sprints opens one sprint's page); the router decides when absent. */
  readonly current?: boolean;
  readonly onNavigate?: () => void;
  /** A branching row (Sprints, Projects): a disclosure at its end opens its recent entries under it, while the row itself still opens its page (2026-09-17, by the owner's request). */
  readonly branch?: SidebarBranch;
  /** Draws the row as one of a branch's entries, indented under the branch's label with no icon. */
  readonly nested?: boolean;
  /** Draws the row as the muted last entry of a branch, the count of what the branch leaves out. */
  readonly muted?: boolean;
  /** A short mark at the row's end with its meaning in words: a client's country as its flag (020 FR-020-173; a flag since 2026-09-20 by the owner's decision), or a word, a portal's closed mark (028 FR-028-047), which takes the width it needs rather than the slot's. */
  readonly tag?: { readonly text: ReactNode; readonly label: string; readonly word?: boolean };
}

export interface SidebarBranch {
  readonly expanded: boolean;
  readonly onExpandedChange: (expanded: boolean) => void;
  /** The disclosure's accessible name, "Show recent sprints" or "Hide recent sprints" as it stands. */
  readonly label: string;
  /** The entries, rendered under the row while it is expanded. */
  readonly children: ReactNode;
}

/** The sidebar lists' shared highlight (`GlideList.tsx`): the row's full width at the row radius in the chrome's hover tone. */
export const SIDEBAR_PILL = "inset-x-0 rounded-(--sidebar-row-radius) bg-chrome-hover";

export function formatCount(count: number, format: (n: number) => string): string {
  return count > 99 ? `${format(99)}+` : format(count);
}

export function SidebarItem({ to, label, icon, count, countLabel, end = false, current, onNavigate, branch, nested = false, muted = false, tag }: SidebarItemProps) {
  const { t, format } = useI18n();
  const Row = current === undefined ? NavLink : Link;
  const entriesId = useId();
  const row = (
    <Row to={to} {...(current === undefined ? { end } : {})} className="b-sidebar__item" data-nested={nested ? "true" : undefined} data-muted={muted ? "true" : undefined} data-glide-row={branch === undefined ? "" : undefined} aria-current={current === undefined ? undefined : current ? "page" : undefined} onClick={onNavigate}>
      {icon !== undefined && (
        <span className="b-sidebar__item-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="b-sidebar__item-label">{label}</span>
      {tag !== undefined && (
        <span className="b-sidebar__trailing" data-word={tag.word === true ? "true" : undefined}>
          <span className="b-sidebar__item-tag" title={tag.label}>
            <span aria-hidden="true">{tag.text}</span>
            <span className="sr-only">{tag.label}</span>
          </span>
        </span>
      )}
      {count !== undefined && count > 0 && (
        <span className="b-sidebar__trailing">
          <span className="b-sidebar__count" aria-label={countLabel ?? t("nav.count", { count })}>
            {formatCount(count, (n) => format.number(n))}
          </span>
        </span>
      )}
    </Row>
  );
  if (branch === undefined) return <li>{row}</li>;
  // The disclosure sits over the row's end, a control beside the link rather than inside it, so the row stays one link and the toggle one button.
  return (
    <li>
      <div className="b-sidebar__branch" data-glide-row>
        {row}
        <button
          type="button"
          className="b-sidebar__disclosure"
          aria-expanded={branch.expanded}
          aria-controls={entriesId}
          aria-label={branch.label}
          onClick={() => {
            branch.onExpandedChange(!branch.expanded);
          }}
        >
          <ChevronRight aria-hidden="true" />
        </button>
      </div>
      <div id={entriesId} className="b-sidebar__entries" hidden={!branch.expanded}>
        {branch.children}
      </div>
    </li>
  );
}

export interface SidebarSectionProps {
  readonly id: string;
  readonly title: string;
  /** Takes the sidebar's height left under the sections above it, down to its foot, with the label staying in place and the rows scrolling on their own once they are taller than that room (the Clients sidebar's clients, 2026-09-28 and 2026-09-29). */
  readonly fill?: boolean;
  /** A quiet control at the label's end that acts on the section's rows, drawn with `SidebarSectionAction` (the Regions label's Expand all and Collapse all, 2026-09-30). */
  readonly action?: ReactNode;
  readonly children: ReactNode;
}

/** The id of a section's rows, for a control that acts on them to name in `aria-controls`. */
export function sidebarSectionRowsId(id: string): string {
  return `sidebar-section-${id}`;
}

/**
 * A titled section of rows; every section is always open (2026-09-17, the collapsible Projects section gone with the sidebar's list of projects).
 * A filling section's rows scroll on their own, and where a scrollbar takes room from them (a classic one;
 * an overlay one takes none) the label gives up the same width, so its control stays over the rows'
 * trailing axis (2026-10-03); the width is measured whenever the rows' box changes, which a scrollbar
 * appearing or going does.
 */
export function SidebarSection({ id, title, fill = false, action, children }: SidebarSectionProps) {
  const [gutter, setGutter] = useState(0);
  const measureGutter = useCallback((rows: HTMLDivElement | null) => {
    if (rows === null) return;
    const observer = new ResizeObserver(() => {
      setGutter(rows.offsetWidth - rows.clientWidth);
    });
    observer.observe(rows);
    return () => {
      observer.disconnect();
    };
  }, []);
  // The rows fade at an edge they continue past (`useScrollFade`, 2026-10-21).
  const rows = useScrollFade({ ref: measureGutter });
  return (
    <section className="b-sidebar__section" data-fill={fill ? "true" : undefined} aria-label={title} style={fill ? ({ "--sidebar-section-gutter": `${gutter}px` } as CSSProperties) : undefined}>
      <div className="b-sidebar__section-header">
        <span className="b-sidebar__section-title">{title}</span>
        {action}
      </div>
      <div id={sidebarSectionRowsId(id)} ref={fill ? rows : undefined} className={fill ? "b-sidebar__section-rows" : undefined}>
        {children}
      </div>
    </section>
  );
}

export interface SidebarSectionActionProps {
  readonly icon: ReactNode;
  /** The accessible name and the tooltip, one text (§ Iconography). */
  readonly label: string;
  /** The section rows' id (`sidebarSectionRowsId`), which the control acts on. */
  readonly controls: string;
  readonly onClick: () => void;
}

/** A section label's control: a bare 16 px glyph in a 24 px square at the label's end, whose hit area keeps the WCAG 2.2 target size without making the label taller (`shell.css`). */
export function SidebarSectionAction({ icon, label, controls, onClick }: SidebarSectionActionProps) {
  return <IconButton size="sm" icon={icon} label={label} className="b-sidebar__section-action" aria-controls={controls} onClick={onClick} />;
}

export interface SidebarProps {
  readonly switcher: ReactNode;
  /** The pinned footer with the actor card, only on a frame with no rail to carry the account card (2026-09-17); the rail's avatar tile is the one place otherwise. */
  readonly footer?: ReactNode;
  /** Names the tree shown: when it changes, the new tree fades in rather than cutting (2026-09-17); the tree of the page's first drawing does not (2026-09-30). */
  readonly treeKey: string;
  /** Whether the sidebar is collapsed, when it is the rail's peek rather than a column (`layout/AppShell.tsx`, 2026-09-17, by the owner's decision); the pin at the end of the switcher's row unpins it, and in the peek pins it open again. Absent in the drawer, which has its own close. */
  readonly collapsed?: boolean;
  readonly onCollapsedChange?: (collapsed: boolean) => void;
  /** The current mode's sections. */
  readonly children: ReactNode;
}

/**
 * Whether a sidebar has drawn since the page loaded. The tree's fade is for a change of what it shows,
 * a mode or the account settings; the page's first drawing, a load or a reload, shows the tree where it
 * stands, since a sidebar that fades and slides in on every refresh is the flash the shell rules out
 * (the owner's meeting of 2026-09-30).
 */
let drawnSinceLoad = false;

export function Sidebar({ switcher, footer, treeKey, collapsed = false, onCollapsedChange, children }: SidebarProps) {
  const { t } = useI18n();
  // The tree the page's first drawing showed, which does not fade; null once a sidebar has drawn, when every tree does.
  const [loadTree] = useState<string | null>(() => (drawnSinceLoad ? null : treeKey));
  useEffect(() => {
    drawnSinceLoad = true;
  }, []);
  // The sidebar fades at an edge its sections continue past (`useScrollFade`, 2026-10-21).
  const scroll = useScrollFade<HTMLElement>();
  return (
    <div className="b-sidebar" data-chrome>
      <nav ref={scroll} className="b-sidebar__scroll" aria-label={t("nav.sidebar")}>
        <div className="b-sidebar__head">
          {switcher}
          {onCollapsedChange !== undefined && (
            <IconButton
              size="sm"
              icon={collapsed ? <Pin /> : <PinOff />}
              label={collapsed ? t("shell.sidebar.pin") : t("shell.sidebar.unpin")}
              className="b-sidebar__collapse"
              aria-expanded={!collapsed}
              onClick={() => {
                onCollapsedChange(!collapsed);
              }}
            />
          )}
        </div>
        <div key={treeKey} className="b-sidebar__tree" data-enter={treeKey === loadTree ? undefined : "true"}>
          {children}
        </div>
      </nav>
      {footer}
    </div>
  );
}
